import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaqTips } from 'timeOffReports/components/FaqTips.component';

import fetchStylistsBySalon from 'actions/stylist/fetchStylistsBySalon.action';
import SLICKPAY_LOGO from 'assets/images/logos/slickpay-new-white.svg';
import IMG_ADMIN from 'assets/slickpay-splash/benefit-admin.png';
import IMG_FEES from 'assets/slickpay-splash/benefit-fees.png';
import IMG_TIPS from 'assets/slickpay-splash/benefit-tips.png';
import Button from 'components/form/Button.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import Space from 'components/generic/Space.component';
import CurrencySign from 'components/price/CurrencySign.component';
import S from 'constants/string.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonId, selectCurrentSalonStylists } from 'selectors/currentSalon.selectors';
import logger from 'utils/logger.util';
import { getActiveStylists } from 'utils/salons/getActiveStylists.util';

import PaymentTabs from './PaymentTabs.component';

import CN from './NoReaderLayout.module.scss';
const L = logger('NoReaderLayout');

const USE_ALERT = false;
const LINK_ORDER = 'https://gettingslick.typeform.com/SlickPayTerms?utm_source=splash';
const LINK_APPLY = 'https://gettingslick.typeform.com/to/wQBtEfq4?utm_campaign=splash';

// NOTE: these values will not be dynamic
const HOURS_SAVED = 10;
const TIP_PER_MONTH = 50;
const FEES_PER_YEAR = 700;
const DEFAULT_RATE = '1.29%';

export const NoReaderLayout = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const stylists = useSelector(selectCurrentSalonStylists);
  const salonId = useSelector(selectCurrentSalonId);

  const { val: isTabsRendered, tip: toggle } = useTip(false);

  const activeStylists = getActiveStylists(stylists);
  const isOneStylist = activeStylists.length <= 1;

  useEffect(() => {
    if (!salonId) return;

    L.unignore('useEffect()', 'fetchStylistsBySalon:', dispatch(fetchStylistsBySalon(salonId)));
  }, [dispatch, salonId]);

  return (
    <div data-bem="NoReaderLayout" className={CN.component}>
      <div className={CN.wrap}>
        <img data-bem="NoReaderLayout__img--splash" src={SLICKPAY_LOGO} alt={t('slickPay.noReader.splash.alt')} className={CN.logo} />
        {/* Benefits section */}
        <div data-bem="SettingSummary__benefit-title" className={CN.benefitsTitle}>
          {t('slickPay.noReader.benefits.title')}
          <Space> {S.creditCard}</Space>
        </div>
        <div data-bem="SettingSummary__benefit-sub-title" className={CN.benefitsSubTitle}>
          {t('slickPay.noReader.benefits.subtitle')}
        </div>
        <div data-bem="SettingSummary__benefit-container" className={CN.benefitsContainer}>
          {/* Free staff tips */}
          <div data-bem="SettingSummary__benefit-tips" className={CN.benefitItem}>
            <div className={CN.textAndImgContainer}>
              <div className={CN.benefitName}> {t('slickPay.noReader.benefits.freeTips.name')}</div>
              <img data-bem="SettingSummary__img--reminders" src={IMG_TIPS} className={CN.imageBenefit} alt="" />
            </div>
            <div className={CN.boxContent}>
              <div className={CN.boxKey}>
                <div className={CN.boxTitle}> {t('slickPay.noReader.benefits.freeTips.boxTitle')}</div>
                <div className={CN.boxValue}>
                  <CurrencySign />
                  {TIP_PER_MONTH}
                </div>
              </div>
            </div>
          </div>

          {/* Low card fees */}
          <div data-bem="SettingSummary__benefit-fees" className={CN.benefitItem}>
            <div className={CN.textAndImgContainer}>
              <div className={CN.benefitName}> {t('slickPay.noReader.benefits.lowFees.name')}</div>
              <img data-bem="SettingSummary__img--marketing" src={IMG_FEES} className={CN.imageBenefit} alt="" />
            </div>
            <div className={CN.boxContent}>
              <div className={CN.boxKey}>
                <div className={CN.boxTitle}> {t('slickPay.noReader.benefits.lowFees.boxTitle')}</div>
                <div className={CN.boxValue}>
                  <CurrencySign />
                  {FEES_PER_YEAR}
                </div>
              </div>
            </div>
          </div>

          {/* No more admin */}
          <div data-bem="SettingSummary__benefit-admin" className={CN.benefitItem}>
            <div className={CN.textAndImgContainer}>
              <div className={CN.benefitName}> {t('slickPay.noReader.benefits.noAdmin.name')}</div>
              <img data-bem="SettingSummary__img--booking" src={IMG_ADMIN} className={CN.imageBenefit} alt="" />
            </div>
            <div className={CN.boxContent}>
              <div className={CN.boxKey}>
                <div className={CN.boxTitle}> {t('slickPay.noReader.benefits.noAdmin.boxTitle')}</div>
                <div className={CN.boxValue}>{HOURS_SAVED}</div>
              </div>
            </div>
          </div>
        </div>
        <div className={CN.feesSection}>
          <div className={CN.feesTitle}> {t('slickPay.noReader.feesSection.title')}</div>

          <div className={CN.feesCards}>
            {/* Left card*/}
            <div className={CN.feesCardPrimary}>
              <div className={CN.cardInner}>
                <div className={CN.cardSubtitle}>{t('slickPay.noReader.feesSection.primaryCard.subtitle')}</div>
                <div className={CN.cardPrice}>{DEFAULT_RATE}</div>
                <div className={CN.cardSmall}>{t('slickPay.noReader.feesSection.primaryCard.priceNote')}</div>
                <div className={CN.cardLinkContainer}>
                  <ExternalLink link={LINK_ORDER} variant="primaryCard">
                    {t('slickPay.noReader.feesSection.primaryCard.cta')}
                  </ExternalLink>
                </div>
              </div>
            </div>

            {/* Right card */}
            <div className={CN.feesCardSecondary}>
              <div className={CN.cardInner}>
                <div className={CN.cardTitle}>{t('slickPay.noReader.feesSection.secondaryCard.title')}</div>

                <div className={CN.cardText}>
                  {t('slickPay.noReader.feesSection.secondaryCard.textLine1')}
                  <br />
                  {t('slickPay.noReader.feesSection.secondaryCard.textLine2')}
                </div>

                <div className={CN.cardLinkContainer}>
                  <ExternalLink link={LINK_APPLY} variant="secondaryCard">
                    {t('slickPay.noReader.feesSection.secondaryCard.cta')}
                  </ExternalLink>
                </div>
              </div>
            </div>
          </div>
        </div>

        {!isOneStylist && (
          <div>
            <div className={CN.tabsSpace}>
              <PaymentTabs />
            </div>
            {USE_ALERT && (
              <div className={CN.panelSpace}>
                <AlertPanel tag="NoReaderLayout__AlertPanel-many-stylists" variant="slick">
                  <div className={CN.alertContainer}>
                    <Icon tag="PaymentTabs__users" variant="buildingPurple" width="xl" />
                    <div className={CN.alertText}>
                      <span className={CN.extraBold}>{t('slickPay.noReader.mixMatch')} </span>
                      {t('slickPay.noReader.paymentMessage')}
                    </div>
                  </div>
                </AlertPanel>
              </div>
            )}
          </div>
        )}
        {isOneStylist ? <div className={CN.gapSm} /> : <div className={CN.gapMd} />}
      </div>
      <div className={CN.wrap}>
        {isOneStylist && (
          <div className={CN.gapLg}>
            <AlertPanel tag="NoReaderLayout__AlertPanel-one-stylist" variant="slick">
              <div className={CN.flex}>
                <div className={CN.alertContainer}>
                  <Icon tag="PaymentTabs__users" variant="users" width="xl" />
                  <div className={CN.alertText}>
                    {t('slickPay.noReader.alertPart1')}
                    <span className={CN.extraBold}>{t('slickPay.noReader.alertPart2')}</span>
                  </div>
                </div>
                <Button onClick={toggle} variant="primaryForce">
                  {t('slickPay.noReader.optionsButton')}
                </Button>
              </div>
            </AlertPanel>

            {isTabsRendered && (
              <div className={CN.margLg}>
                <PaymentTabs />
              </div>
            )}
          </div>
        )}
      </div>

      <div className={CN.wrap}>
        <FaqTips isDark />
      </div>
    </div>
  );
};
